import { useEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"
import { graphql, PreloadedQuery, useFragment, usePreloadedQuery } from "react-relay"
import { Banner } from "@/components/ui/banner"
import { cn } from "@/lib/utils"
import { urgentAcuteAppointmentWarning_waitingRoomDay$key } from "@/modules/waiting-room/components/appointment-table/__generated__/urgentAcuteAppointmentWarning_waitingRoomDay.graphql"
import { useAppointmentContext } from "@/modules/waiting-room/contexts/appointment-context"
import { useWaitingRoomFiltersContext } from "@/modules/waiting-room/contexts/waiting-room-filters-context"
import { WaitingRoomAppointmentsQuery } from "@/modules/waiting-room/services/__generated__/WaitingRoomAppointmentsQuery.graphql"
import { waitingRoomQuery } from "@/modules/waiting-room/services/WaitingRoomAppointmentsQuery"

const UrgentAcuteAppointmentWarningFragment = graphql`
  fragment urgentAcuteAppointmentWarning_waitingRoomDay on WaitingRoomDay @relay(plural: true) {
    slots {
      appointments {
        appointment {
          urgency
        }
      }
    }
  }
`

interface UrgentAcuteAppointmentWarningProps {
  queryReference: PreloadedQuery<WaitingRoomAppointmentsQuery>
}

const UrgentAcuteAppointmentWarning = ({ queryReference }: UrgentAcuteAppointmentWarningProps) => {
  const { t } = useTranslation()

  const hasAnyUrgentAcuteAppointment = useAppointmentContext((state) => state.hasAnyUrgentAcuteAppointment)
  const setHasAnyUrgentAcuteAppointment = useAppointmentContext((state) => state.setHasAnyUrgentAcuteAppointment)
  const hasScrolledOverFirstUrgentAcuteAppointment = useAppointmentContext(
    (state) => state.hasScrolledOverFirstUrgentAcuteAppointment,
  )

  const filters = useWaitingRoomFiltersContext((state) => state.filters)

  const data = usePreloadedQuery<WaitingRoomAppointmentsQuery>(waitingRoomQuery, queryReference)

  const daySlots = useFragment<urgentAcuteAppointmentWarning_waitingRoomDay$key>(
    UrgentAcuteAppointmentWarningFragment,
    data.waitingRoomAppointments.waitingRoomDays,
  )

  const filteredAppointments = useMemo(
    () =>
      daySlots.map((item) => ({
        ...item,
        slots: item.slots.filter((slot) => slot.appointments.length > 0),
      })),
    [daySlots],
  )

  useEffect(() => {
    const hasAnyUrgentAcuteAppointment =
      filteredAppointments.length > 0 &&
      filteredAppointments[0].slots.length > 0 &&
      filteredAppointments[0].slots[0].appointments[0].appointment.urgency !== "NORMAL"

    setHasAnyUrgentAcuteAppointment(hasAnyUrgentAcuteAppointment)
  }, [filteredAppointments, setHasAnyUrgentAcuteAppointment])

  return (
    <div
      className={cn(
        "bg-background-subdued mt-4 grid gap-2 overflow-hidden px-8 transition-all duration-200",
        hasAnyUrgentAcuteAppointment && hasScrolledOverFirstUrgentAcuteAppointment && !filters.dateRange
          ? "h-10"
          : "h-0",
      )}>
      <Banner type="inline" variant="critical" data-testid="medical-acute-or-urgent-hidden-banner">
        {t("scheduling.waitingRoom.medicalAcuteOrUrgentHidden")}
      </Banner>
    </div>
  )
}
export { UrgentAcuteAppointmentWarning }
